import {getTestProps} from '@benchling/js-test-utils';
import {Text} from '@mantine/core';
import React from 'react';

import * as ArchiveActions from 'archive/ArchiveActions';
import {ArchiveRecord} from 'archive/ArchiveConstants';
import archiveMenuOption from 'archive/components/buttons/ArchiveMenuOption';
import ArchiveOrUnarchiveButton from 'archive/components/buttons/ArchiveOrUnarchiveButton';
import {ArchiveModalItem} from 'archive/components/modals/ArchiveModal';
import isArchived from 'archive/isArchived';
import {exportAuditLogs} from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING, EXPORT_RESULT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import {StandardFeature} from 'auth/capabilities/CapabilityConstants';
import {
  InventoryFeature,
  RegistryFeature,
  StandardFeatureDefinitionWithItem,
} from 'auth/capabilities/CapabilityFeatureConstants';
import {areAnyFeaturesEnabled, isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {NumberIDFolder, StringIDFolder} from 'constants/FolderConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import FolderSelectorModal from 'file-browser/FolderSelectorModal';
import {getExportURL} from 'folder-items/FileUtil';
import * as BioentityActions from 'inventory/BioentityActions';
import PrintBarcodeModal from 'label-printing/PrintBarcodeModal';
import {evaluateLDFlag} from 'launchdarkly/LaunchDarklyUtil';
import ExportMoleculeModal from 'metadata/ExportMoleculeModal';
import ExportSequenceModal from 'metadata/ExportSequenceModal';
import MetadataToggleRegisterButton from 'metadata/registry/MetadataToggleRegisterButton';
import useIsComplexPolymerQuery from 'modbio/complex-polymers/useIsComplexPolymerQuery';
import RegistryActions from 'registry/RegistryActions';
import {openSetCollectionModal} from 'registry/SetCollectionModal';
import EntityStore from 'stores/EntityStore';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import legacyRunModal from 'ui-library/legacyRunModal';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option, {OptionProps} from 'ui-library/menus/Option';
import licenses from 'user/Licenses';
import APIUtil from 'util/APIUtil';
import {submitCSRFForm} from 'util/Net';
import AddToWorklistButton from 'worklists/AddToWorklistButton';
import {allIdsSupported} from 'worklists/WorklistUtil';

export interface MetadataEditorToolbarProps {
  archiveRecord?: ArchiveRecord | null;
  fileId: string;
  fileName: string;
  folder?: NumberIDFolder | StringIDFolder;
  hasRegistrySupport: boolean;
  isBioentityFailed: boolean;
  onRegister: () => void;
  permissions: Permissions;
  projectFolder?: NumberIDFolder | StringIDFolder | null;
  registryId: string | null;
  schemaId?: string | null;
  schemaRegistryId: string | null;
  schemaPermissions: {objectsRegisterable: boolean} | null;
  additionalMenuItems?: Array<Omit<OptionProps<string>, 'shouldEllipsize'>>;
  editURL?: string;
  shouldAllowChangeCollection: boolean;
  disableRegistering: boolean;

  // Whether or not the object is a non-interactive Process (aka TaskGroup)
  // Non-interactive processes cannot be edited or archived. The only edit action available is to move the process
  // to a different folder as an escape hatch to handle permissions issues.
  isNonInteractiveProcess?: boolean;
}

export class MetadataEditorToolbar extends React.Component<MetadataEditorToolbarProps> {
  private handleChangeCollection = (): void => {
    openSetCollectionModal({files: [{id: this.props.fileId, name: this.props.fileName}]});
  };

  private isRegistered = (): boolean => this.props.registryId != null;

  // When enabled, Archive and Unregister are relocated from inline toolbar buttons into the overflow
  // ("More") menu (Unregister directly above Archive). Mirrors how the expanded folder toolbar
  // consumes the same rollout flag.
  private moveArchiveAndUnregisterToMore = (): boolean =>
    evaluateLDFlag('UX_MOVE_ARCHIVE_AND_UNREGISTER_TO_MORE', false) === true;

  private getPartialFeatureDefinition = (
    permissions?: Permissions
  ): Omit<StandardFeatureDefinitionWithItem, 'feature'> => ({
    item: {
      id: this.props.fileId,
      permissions,
    },

    itemState: {isRegistered: this.isRegistered()},
  });

  private handleOpenForMove = (isUnregister: boolean): void => {
    const originalFolder = this.isRegistered() ? this.props.projectFolder : this.props.folder;
    const files = [{id: this.props.fileId}];
    const options = {
      allowNone:
        this.isRegistered() && !BenchlingConfig.ENABLE_PROJECTS_REQUIRED_FOR_REGISTRY_FILES && !isUnregister,
      initialSelectedFolderId: originalFolder ? originalFolder.api_identifier : undefined,
      itemsToMove: files,
      showInventory: true,
      showNotebook: true,
      showRegistries: false,
    };
    const onSelectFolder = (folderId: string): void => {
      const parentFolderIds = [this.props.folder!.api_identifier];
      if (isUnregister) {
        if (this.props.projectFolder != null) {
          parentFolderIds.push(this.props.projectFolder.api_identifier);
        }
        RegistryActions.unregisterWithDataMods([this.props.fileId], folderId, parentFolderIds);
      } else {
        FileBrowserActions.moveItemsWithDataMods(
          [this.props.fileId],
          folderId,
          {parentFolderIds, hasRegisteredItems: this.isRegistered()},
          // `shortcutPredicate` - if the file is registered, tell `moveItems` to change project shortcut
          () => this.isRegistered()
        );
      }
    };
    // Unregister presents the folder picker as a first-class "Unregister" action rather than the
    // generic "Move To..." modal.
    if (isUnregister) {
      FolderSelectorModal.openForUnregister(options, onSelectFolder);
    } else {
      FolderSelectorModal.openForMove(options, onSelectFolder);
    }
  };

  private makeExempt = (): void => {
    BioentityActions.overrideValidationStatus([this.props.fileId]);
  };

  private getArchiveItem = (): ArchiveModalItem => ({
    id: this.props.fileId,
    name: this.props.fileName,
    archiveRecord: this.props.archiveRecord,
    projectFolder: this.props.projectFolder,
    isRegistered: this.isRegistered(),
  });

  private shouldShowArchiveOrUnarchive = (): boolean =>
    areAnyFeaturesEnabled([
      {
        feature: StandardFeature.ARCHIVE,
        ...this.getPartialFeatureDefinition(this.props.permissions),
      },
      {
        feature: StandardFeature.UNARCHIVE,
        ...this.getPartialFeatureDefinition(this.props.permissions),
      },
    ]) &&
    !this.props.isNonInteractiveProcess &&
    // Don't show archive button on metadata panel if flag is not enabled
    !(APIUtil.isTemplateCollectible(this.props.fileId) && !BenchlingConfig.ELN_ENABLE_TEMPLATE_ARCHIVING) &&
    // Write actions on existing molecules should be disabled when chemistry is disabled
    (!APIUtil.isMolecule(this.props.fileId) || BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE);

  private canUnregister = (): boolean =>
    this.props.registryId != null &&
    isFeatureEnabled({
      feature: RegistryFeature.REGISTRY_UNREGISTER,
      item: {id: this.props.fileId, permissions: this.props.permissions},
    });

  private renderMoreFileOptions = (): JSX.Element | null => {
    const menuItems: Array<JSX.Element> = [];
    if (APIUtil.isNucleotideSequence(this.props.fileId)) {
      menuItems.push(
        <Option
          key="export-sequence"
          onSelect={() => {
            legacyRunModal(ExportSequenceModal, {editURL: this.props.editURL!});
          }}
          dataTestAttrs={{
            component: 'OverflowDropdownButton',
            element: 'exportSequence',
          }}
        >
          Export sequence
        </Option>
      );
    }
    if (APIUtil.isMolecule(this.props.fileId)) {
      menuItems.push(
        <Option
          key="export-molecule"
          onSelect={() => {
            legacyRunModal(ExportMoleculeModal, {editURL: this.props.editURL!});
          }}
          dataTestAttrs={{component: 'OverflowDropdownButton', element: 'exportMolecule'}}
        >
          Export molecule
        </Option>
      );
    }
    if (APIUtil.isBulkAssembly(this.props.fileId)) {
      menuItems.push(
        // TODO(BNCH-59006): Actually do export
        <Option
          key="export-assembly"
          isDisabled={true}
          tooltipTitle="Export is not yet available for Assemblies."
        >
          Export assembly
        </Option>
      );
    }
    if (licenses.hasEnterprisePlan()) {
      menuItems.push(
        <Option
          key="export-audit-log"
          onSelect={() => {
            exportAuditLogs([this.props.fileId]);
          }}
          dataTestAttrs={{component: 'OverflowDropdownButton', element: 'exportAuditLog'}}
        >
          {EXPORT_AUDIT_LOG_DISPLAY_STRING}
        </Option>
      );
    }
    if (licenses.hasEnterprisePlan() && APIUtil.isBiologicalEntity(this.props.fileId)) {
      menuItems.push(
        <Option
          key="export-result-audit-log"
          onSelect={() => {
            exportAuditLogs([], {relatedResultsModelId: this.props.fileId});
          }}
        >
          {EXPORT_RESULT_AUDIT_LOG_DISPLAY_STRING}
        </Option>
      );
    }
    if (this.props.permissions.admin && this.props.isBioentityFailed) {
      menuItems.push(
        <Option
          key="override-validation"
          onSelect={() => {
            this.makeExempt();
          }}
        >
          Override validation checks
        </Option>
      );
    }
    if (isFeatureEnabled({feature: InventoryFeature.INVENTORY_EXPORT}) && this.props.registryId) {
      menuItems.push(
        <Option
          key="print-barcode"
          onSelect={() => {
            legacyRunModal(PrintBarcodeModal, {itemIds: [this.props.fileId]});
          }}
        >
          Print registry labels
        </Option>
      );
    }
    (this.props.additionalMenuItems ?? []).forEach((optionProps) => {
      menuItems.push(<Option {...optionProps} key={optionProps.value} />);
    });

    // Destructive actions relocated into the More menu, grouped at the end with Unregister directly
    // above Archive. Gated by the rollout flag; otherwise these remain inline toolbar buttons.
    if (this.moveArchiveAndUnregisterToMore()) {
      if (this.props.hasRegistrySupport && this.canUnregister()) {
        menuItems.push(
          <Option
            key="unregister"
            onSelect={() => {
              this.handleOpenForMove(true /* isUnregister */);
            }}
            dataTestAttrs={{component: 'OverflowDropdownButton', element: 'unregister'}}
          >
            {/* Unregister is destructive, so its menu entry is styled red to match the equivalent
                action in the expanded folder toolbar's More menu. */}
            <Text span={true} c="red.8">
              Unregister
            </Text>
          </Option>
        );
      }
      // Only the destructive Archive action relocates into More. Unarchive stays inline (handled by
      // the toolbar button below), matching the expanded folder toolbar, so it is excluded here.
      if (this.shouldShowArchiveOrUnarchive() && !isArchived(this.getArchiveItem())) {
        const archiveOption = archiveMenuOption({
          isArchivable: Boolean(this.props.permissions.archivable),
          item: this.getArchiveItem(),
          onArchive: ArchiveActions.bulkArchiveFilesWithDataMods,
        });
        if (archiveOption) {
          menuItems.push(archiveOption);
        }
      }
    }

    if (menuItems.length) {
      return (
        <Dropdown
          menu={
            <Menu dropLeft={true} height={MenuHeight.MEDIUM} width={MenuWidth.FIT_CONTENT}>
              {...menuItems}
            </Menu>
          }
          tooltip="More"
        >
          <Button
            aria-label="More"
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            hideDropdownCaret={true}
            className="metadata-header-button-spacing-left"
            icon="overflow"
            isDropdown={true}
            size={ButtonSize.SMALL}
          />
        </Dropdown>
      );
    }
    return null;
  };

  override render(): JSX.Element {
    return (
      <div
        className="metadata-header-section u-justify-content-end"
        {...getTestProps({
          component: 'MetadataEditorToolbar',
          element: 'root',
        })}
      >
        {this.props.registryId &&
          this.props.shouldAllowChangeCollection &&
          isFeatureEnabled({...this.getPartialFeatureDefinition(), feature: StandardFeature.EDIT}) && (
            <Tooltip container="body" placement={TooltipPlacement.TOP} title="Change collection...">
              <Button
                aria-label="Change collection"
                buttonStyle={ButtonStyle.TERTIARY_GRAY}
                className="metadata-header-button-spacing-left"
                icon="add-collection"
                onClick={this.handleChangeCollection}
                size={ButtonSize.SMALL}
              />
            </Tooltip>
          )}
        {APIUtil.isFolderContainable(this.props.fileId) &&
          isFeatureEnabled({
            feature: StandardFeature.MOVE,
            ...this.getPartialFeatureDefinition(),
          }) && (
            <Tooltip container="body" placement={TooltipPlacement.TOP} title="Move to folder">
              <Button
                aria-label="Move to folder"
                buttonStyle={ButtonStyle.TERTIARY_GRAY}
                className="metadata-header-button-spacing-left"
                disabled={!this.props.permissions.movable}
                icon="move"
                onClick={() => {
                  this.handleOpenForMove(false);
                }}
                size={ButtonSize.SMALL}
              />
            </Tooltip>
          )}
        {BenchlingConfig.WORKLISTS_ENABLE_WORKLISTS && allIdsSupported([this.props.fileId]) && (
          <AddToWorklistButton
            className="u-mini-spacing-left"
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            disabled={!EntityStore.currentUser()}
            ids={[this.props.fileId]}
            refreshOptions={{refreshRecentWorklists: true}}
          />
        )}
        {this.props.hasRegistrySupport && (
          <MetadataToggleRegisterButton
            fileId={this.props.fileId}
            permissions={this.props.permissions}
            registryId={this.props.registryId}
            schemaRegistryId={this.props.schemaRegistryId}
            schemaPermissions={this.props.schemaPermissions}
            disableRegistering={this.props.disableRegistering}
            onRegister={this.props.onRegister}
            onUnregister={() => {
              this.handleOpenForMove(true /* isUnregister */);
            }}
            hideUnregisterButton={this.moveArchiveAndUnregisterToMore()}
          />
        )}
        {/* When the flag relocates destructive actions, only Archive moves into More; the inline
            button stays for archived items so Unarchive remains a first-class inline action. */}
        {(!this.moveArchiveAndUnregisterToMore() || isArchived(this.getArchiveItem())) &&
          this.shouldShowArchiveOrUnarchive() && (
            <ArchiveOrUnarchiveButton
              className="metadata-header-button-spacing-left"
              isArchivable={Boolean(this.props.permissions.archivable)}
              isUnarchivable={Boolean(this.props.permissions.unarchivable)}
              item={this.getArchiveItem()}
              onArchive={ArchiveActions.bulkArchiveFilesWithDataMods}
              onUnarchive={ArchiveActions.bulkUnarchiveFilesWithDataMods}
            />
          )}
        {this.renderMoreFileOptions()}
      </div>
    );
  }
}

export default function QueryingMetadataEditorToolbar(props: MetadataEditorToolbarProps): JSX.Element {
  const additionalMenuItems: Array<Omit<OptionProps<string>, 'shouldEllipsize'>> = [];
  const isComplexPolymer = useIsComplexPolymerQuery(props.fileId, props.schemaId ?? null);
  if (isComplexPolymer) {
    additionalMenuItems.push({
      children: 'Export as HELM',
      value: 'export-as-helm',
      onSelect: () => {
        const exportURL = getExportURL(props.editURL!, 'helm', {dl: true});
        submitCSRFForm('POST', exportURL, {target: '_blank'});
      },
    });
  }
  return (
    <MetadataEditorToolbar
      {...props}
      additionalMenuItems={[...(props.additionalMenuItems ?? []), ...additionalMenuItems]}
    />
  );
}
